import { DocumentSource, QuoteDocumentConfig } from '@monetize/types/app';
import { FC, useState } from 'react';
import {
  MBox,
  MButton,
  MCheckbox,
  MFlex,
  MSpinner,
  MText,
} from '../../../../../components/Monetize';
import MPopoverWrapper from '../../../../../components/Monetize/MPopoverWrapper';
import { useFlags } from '../../../../../services/launchDarkly';
import { buildImportPayload } from '../quoteDocumentsListUtils';

interface SelectQuoteDocumentsPopoverProps {
  availableDocuments: QuoteDocumentConfig[];
  isLoading: boolean;
  isImporting: boolean;
  onAdd: (items: ReturnType<typeof buildImportPayload>) => Promise<void> | void;
}

export const SelectQuoteDocumentsPopover: FC<
  SelectQuoteDocumentsPopoverProps
> = ({ availableDocuments, isLoading, isImporting, onAdd }) => {
  const { enableTenantQuoteDocumentManagement } = useFlags();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const items = availableDocuments.filter(
    (doc) =>
      enableTenantQuoteDocumentManagement ||
      doc.originalSource === DocumentSource.LEGAL_ENTITY,
  );

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedIds(new Set());
  };

  const handleAdd = async () => {
    const toImport = buildImportPayload(selectedIds, items);
    if (toImport.length === 0) {
      return;
    }
    handleClose();
    await onAdd(toImport);
  };

  return (
    <MPopoverWrapper
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={handleClose}
      placement="bottom-start"
      popoverProps={{ lazyMount: true, unmountOnExit: true }}
      popoverContentProps={{ width: '420px' }}
      triggerContent={
        <MButton
          size="sm"
          variant="secondary"
          data-testid="quote-select-documents-btn"
          isLoading={isImporting}
        >
          + Select documents
        </MButton>
      }
      headerContent={
        <MText fontWeight="semibold" fontSize="sm">
          From the library
        </MText>
      }
      bodyContent={
        <MBox maxH="220px" minH="50px" overflow="auto">
          {items.length === 0 && !isLoading && (
            <MText
              fontSize="sm"
              color="tGray.darkGrayPurple"
              textAlign="center"
              fontStyle="italic"
            >
              All library documents have been added to the quote.
            </MText>
          )}
          {items.map((doc) => (
            <MFlex
              key={doc.id}
              alignItems="flex-start"
              gap={3}
              py={2}
              px={1}
              borderBottom="1px solid"
              borderColor="tGray.lightGray"
              cursor="pointer"
              onClick={() => toggle(doc.id)}
            >
              <MCheckbox
                mt="0.5"
                isChecked={selectedIds.has(doc.id)}
                onChange={() => toggle(doc.id)}
                onClick={(event) => event.stopPropagation()}
                aria-label={`Select ${doc.storageObject?.filename ?? 'document'}`}
              />
              <MFlex direction="column" gap={0.5} flex={1} minW={0}>
                <MText
                  color="tPurple.dark"
                  fontSize="sm"
                  fontWeight="medium"
                  truncate
                >
                  {doc.storageObject?.filename || '-'}
                </MText>
                <MText fontSize="xs" color="fg.muted" truncate>
                  {doc.quoteDocumentType.label}
                </MText>
              </MFlex>
            </MFlex>
          ))}
          {isLoading && (
            <MFlex justifyContent="center" py={4}>
              <MSpinner />
            </MFlex>
          )}
        </MBox>
      }
      footerContent={
        items.length > 0 ? (
          <MFlex alignItems="center" justifyContent="space-between" w="100%">
            <MText fontSize="xs" color="tGray.darkPurple">
              {selectedIds.size} selected
            </MText>
            <MButton
              variant="primary"
              size="sm"
              isDisabled={selectedIds.size === 0 || isImporting}
              isLoading={isImporting}
              onClick={handleAdd}
              data-testid="quote-select-documents-add-btn"
            >
              Add
            </MButton>
          </MFlex>
        ) : undefined
      }
    />
  );
};
